/**
 * useEmployeeDashboard — single integration point for every GraphQL query
 * the Employee Dashboard depends on.
 *
 * Purpose
 * -------
 * The dashboard page should stay pure presentation: it composes section
 * components and feeds them props. All data fetching lives here so:
 *   - the page is easy to read and re-arrange without touching network code,
 *   - the same data shape can be reused by tests or alternate layouts,
 *   - new queries get added in one place as the dashboard grows.
 */
import { useQuery } from "@apollo/client";
import CardServerStatus from "enums/CardStatusType";
import CardType from "enums/CardType";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import ViolationType from "enums/ViolationType";
import { MY_CARDS } from "graphql/card";
import {
  GET_TRANSACTION_STATUSES,
  TRANSACTION_TASKS_BY_COMPLIANCE,
} from "graphql/transaction";
import {
  OUTSTANDING_PERSONAL_STATUSES,
  OUTSTANDING_REIMBURSEMENT_STATUSES,
  REIMBURSEMENT_TYPES,
} from "utils/transactions/transactionGroups";
import { requestCounts } from "utils/transactions/requestCounts";
import { Merchant } from "components/common/MerchantSearchAutoComplete";
import ViolationStatus from "enums/ViolationStatus";
import type {
  CardPolicyLimits,
  CardSpendingSummary,
} from "utils/cards/cardRowData";
import { ComplianceStatusDisplay } from "components/common/RenderCompliance";

export interface DashboardCard {
  id: string;
  nickname?: string | null;
  type: CardType;
  cardNumber?: string | null;
  currency?: string | null;
  status: CardServerStatus;
  spendingSummary?: CardSpendingSummary | null;
  activePolicy?: CardPolicyLimits | null;
}

/** Count of outstanding requests in one category, split by status. */
export interface RequestStatusCounts {
  pending: number;
  processing: number;
  total: number;
  /** More than the fetch limit matched, so `total` stopped there. */
  capped: boolean;
}

export interface OutstandingRequestCounts {
  reimbursements: RequestStatusCounts;
  personalExpenses: RequestStatusCounts;
}

/**
 * The limit for fetching outstanding requests.
 * TODO: Make this dynamic based on the company settings.
 */
const OUTSTANDING_FETCH_LIMIT = 100;

/** Per-category compliance violation, as consumed by `RenderCompliance`. */
interface ViolationDetail {
  violationStatus?: ViolationStatus | null;
  isExempt?: boolean | null;
}

export interface PolicyStatus {
  receipt?: ViolationDetail | null;
  allocation?: ViolationDetail | null;
}

export interface Transaction {
  id: string;
  merchantData: Merchant;
  amount: number;
  currency?: string | null;
  transactionDate?: number | null;
  status?: TransactionStatus | null;
  type: TransactionsType;
  card?: {
    id: string;
    cardNumber?: string | null;
    nickname?: string | null;
  } | null;
  policyStatus?: PolicyStatus | null;
  policyStatusDisplay: ComplianceStatusDisplay;
}

/**
 * Raw connection edge — both the edge and its node are nullable in the
 * GraphQL schema, so consumers must narrow before treating `node` as a
 * `Transaction`.
 */
interface TransactionEdge {
  node?: Transaction | null;
}

interface TransactionConnectionData {
  transactions?: {
    edges?: TransactionEdge[] | null;
    pageInfo?: {
      total?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
}

/**
 * Exact task count from the connection's `pageInfo.total` (the resolver
 * computes it over the full non-exempt set via `setComplianceConditions`), so
 * it isn't capped by the fetched page size the way counting edges would be.
 */
interface TaskConnectionData {
  transactionTasks?: { pageInfo?: { total?: number | null } | null } | null;
}

const taskTotal = (data: TaskConnectionData | undefined): number =>
  data?.transactionTasks?.pageInfo?.total ?? 0;

/**
 * Per-query loading flags. Use these when a section can render as soon
 * as its own data has returned, instead of waiting for the aggregate
 * `isLoading` to flip false.
 *
 * @example
 * const { cards, loading, isLoading } = useEmployeeDashboard();
 * if (loading.cards) return <CardsSkeleton />;     // render cards independently
 * if (isLoading) return <FullPageSkeleton />;      // wait on everything
 */
export interface LoadingStates {
  cards: boolean;
  receipts: boolean;
  allocations: boolean;
  outstandingRequests: boolean;
}

/** Per-query error flags. `true` when the matching query failed. */
export interface ErrorStates {
  cards: boolean;
  receipts: boolean;
  allocations: boolean;
  outstandingRequests: boolean;
}

export interface EmployeeDashboardData {
  cards: DashboardCard[];
  /** Cards shown in the "My Cards" section — ACTIVE, FROZEN, PENDING. */
  myCards: DashboardCard[];
  /** Cards awaiting activation — drives the activate-card banner. */
  issuedCards: DashboardCard[];
  /** Outstanding reimbursement & personal-expense counts for the panel. */
  outstandingRequests: OutstandingRequestCounts;
  /** Count of transactions missing a receipt */
  missingReceiptsCount: number;
  /** Count of transactions missing a cost allocation */
  missingAllocationsCount: number;
  /** Sum of the two compliance counts. */
  totalTasks: number;
  /** True until every underlying query has returned. */
  isLoading: boolean;
  /** Per-query loading flags for sections that can render independently. */
  loading: LoadingStates;
  /** Per-query error flags. Sections should fail-closed when their flag is set. */
  errors: ErrorStates;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useEmployeeDashboard(): EmployeeDashboardData {
  const {
    data: cardsData,
    loading: cardsLoading,
    error: cardsError,
  } = useQuery(MY_CARDS, {
    variables: {
      cardStatus: [
        CardServerStatus.ACTIVE,
        CardServerStatus.FROZEN,
        CardServerStatus.PENDING,
        CardServerStatus.ISSUED,
      ],
    },
  });

  const {
    data: missingReceiptsData,
    loading: receiptsLoading,
    error: receiptsError,
  } = useQuery(TRANSACTION_TASKS_BY_COMPLIANCE, {
    variables: {
      compliance: ViolationType.MISSING_RECEIPT,
    },
  });

  const {
    data: missingAllocationsData,
    loading: allocationsLoading,
    error: allocationsError,
  } = useQuery(TRANSACTION_TASKS_BY_COMPLIANCE, {
    variables: {
      compliance: ViolationType.MISSING_COST_ALLOCATION,
    },
  });

  // The lists the tile's rows open use these statuses too, so each count is the list it opens.
  const {
    data: reimbursementsData,
    loading: reimbursementsLoading,
    error: reimbursementsError,
  } = useQuery(GET_TRANSACTION_STATUSES, {
    variables: {
      first: OUTSTANDING_FETCH_LIMIT,
      mine: true,
      type: REIMBURSEMENT_TYPES,
      transactionStatus: OUTSTANDING_REIMBURSEMENT_STATUSES,
    },
  });

  const {
    data: personalExpensesData,
    loading: personalExpensesLoading,
    error: personalExpensesError,
  } = useQuery(GET_TRANSACTION_STATUSES, {
    variables: {
      first: OUTSTANDING_FETCH_LIMIT,
      mine: true,
      transactionStatus: OUTSTANDING_PERSONAL_STATUSES,
    },
  });

  const cards: DashboardCard[] = (cardsData?.cards?.edges ?? []).map(
    (edge: { node: DashboardCard }) => edge.node,
  );

  const statusesOf = (data?: TransactionConnectionData) =>
    (data?.transactions?.edges ?? []).map((edge) => edge?.node?.status);

  const outstandingRequests: OutstandingRequestCounts = {
    reimbursements: {
      ...requestCounts(
        statusesOf(reimbursementsData),
        TransactionStatus.PROCESSING_PAYMENT,
        reimbursementsData?.transactions?.pageInfo?.total,
      ),
      capped: Boolean(reimbursementsData?.transactions?.pageInfo?.hasNextPage),
    },
    personalExpenses: {
      ...requestCounts(
        statusesOf(personalExpensesData),
        TransactionStatus.PERSONAL_PROCESSING,
        personalExpensesData?.transactions?.pageInfo?.total,
      ),
      capped: Boolean(
        personalExpensesData?.transactions?.pageInfo?.hasNextPage,
      ),
    },
  };

  const missingReceiptsCount = taskTotal(missingReceiptsData);
  const missingAllocationsCount = taskTotal(missingAllocationsData);
  const totalTasks = missingReceiptsCount + missingAllocationsCount;

  const myCards = cards.filter(
    (c) =>
      c.status === CardServerStatus.ACTIVE ||
      c.status === CardServerStatus.FROZEN ||
      c.status === CardServerStatus.PENDING,
  );

  const issuedCards = cards.filter(
    (c) => c.status === CardServerStatus.ISSUED && c.type === CardType.PHYSICAL,
  );

  const loading: LoadingStates = {
    cards: cardsLoading,
    receipts: receiptsLoading,
    allocations: allocationsLoading,
    outstandingRequests: reimbursementsLoading || personalExpensesLoading,
  };

  const errors: ErrorStates = {
    cards: Boolean(cardsError),
    receipts: Boolean(receiptsError),
    allocations: Boolean(allocationsError),
    outstandingRequests: Boolean(reimbursementsError || personalExpensesError),
  };

  const isLoading =
    cardsLoading ||
    receiptsLoading ||
    allocationsLoading ||
    reimbursementsLoading ||
    personalExpensesLoading;

  return {
    cards,
    myCards,
    issuedCards,
    outstandingRequests,
    missingReceiptsCount,
    missingAllocationsCount,
    totalTasks,
    isLoading,
    loading,
    errors,
  };
}
